import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { useTableContext } from './TableContext'

export const Navigation = () => {
  // Use the context
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    companyData,
    isLoading,
    goToNextPage,
    goToPreviousPage,
  } = useTableContext()

  // Get the isFetching state from the context
  const isFetching = isLoading

  // Determine what pagination buttons to show
  const renderPaginationButtons = () => {
    if (!companyData?.totalPages) return null

    const totalPages = companyData.totalPages

    // For mobile screens, show fewer page buttons
    const isMobile = window.innerWidth < 640
    const maxButtonsToShow = isMobile ? 3 : 7

    let startPage = Math.max(1, currentPage - Math.floor(maxButtonsToShow / 2))
    const endPage = Math.min(totalPages, startPage + maxButtonsToShow - 1)

    // Adjust start page if we're near the end
    if (endPage - startPage < maxButtonsToShow - 1) {
      startPage = Math.max(1, endPage - (maxButtonsToShow - 1))
    }

    const pages = []

    // Add first page button and ellipsis if needed
    if (startPage > 1) {
      pages.push(
        <Button
          aria-label="Primera página"
          className="h-8 w-8 bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching}
          key="first"
          onClick={() => setCurrentPage(1)}
          size="sm"
          variant="outline"
        >
          1
        </Button>
      )

      if (startPage > 2) {
        pages.push(
          <span
            aria-hidden="true"
            className="px-1 text-muted-foreground"
            key="ellipsis-start"
          >
            ...
          </span>
        )
      }
    }

    // Add page buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          aria-current={currentPage === i ? 'page' : undefined}
          aria-label={`Página ${i}`}
          className={`h-8 w-8 ${
            currentPage === i
              ? 'shadow-sm'
              : 'bg-background text-foreground hover:bg-accent hover:text-accent-foreground'
          }`}
          disabled={isFetching}
          key={i}
          onClick={() => setCurrentPage(i)}
          size="sm"
          variant={currentPage === i ? 'default' : 'outline'}
        >
          {i}
        </Button>
      )
    }

    // Add last page button and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span
            aria-hidden="true"
            className="px-1 text-muted-foreground"
            key="ellipsis-end"
          >
            ...
          </span>
        )
      }

      pages.push(
        <Button
          aria-label={`Última página, página ${totalPages}`}
          className="h-8 w-8 bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching}
          key="last"
          onClick={() => setCurrentPage(totalPages)}
          size="sm"
          variant="outline"
        >
          {totalPages}
        </Button>
      )
    }

    return pages
  }

  // Render skeleton pagination during loading
  const renderSkeletonPagination = () => {
    // Approximate the same space that will be taken by the pagination buttons
    const buttonCount = 5 // Assuming we show about 5 buttons on average

    return (
      <div
        className="flex items-center gap-1"
        style={{ minWidth: `${buttonCount * 40}px` }}
      >
        {Array.from({ length: buttonCount }).map((_, i) => (
          <Skeleton className="h-8 w-8 rounded-md" key={i} />
        ))}
      </div>
    )
  }

  // Don't show pagination if there are no results or only one page
  if (isLoading) {
    return (
      <div className="flex items-center justify-between border-t px-4 py-4">
        <div className="min-w-[300px]">
          <Skeleton className="h-5 w-56" />
        </div>
        <div className="flex items-center gap-1">
          <Skeleton className="h-8 w-8 rounded-md" />
          {renderSkeletonPagination()}
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
    )
  }

  if (!companyData?.totalItems || companyData.totalPages <= 1) {
    return (
      <div className="flex items-center justify-end border-t px-4 py-4">
        <span className="text-sm text-muted-foreground">
          {companyData?.totalItems
            ? `${companyData.totalItems} resultados`
            : 'No hay resultados'}
        </span>
      </div>
    )
  }

  return (
    <nav
      aria-label="Paginación"
      className="flex flex-col items-center justify-between gap-2 border-t px-4 py-4 sm:flex-row"
    >
      <div className="order-2 min-w-[300px] text-center text-sm text-muted-foreground sm:order-1 sm:text-left">
        {isFetching ? (
          <Skeleton className="h-5 w-56" />
        ) : (
          <>
            Mostrando {(currentPage - 1) * pageSize + 1} al{' '}
            {Math.min(currentPage * pageSize, companyData.totalItems)} de{' '}
            {companyData.totalItems} resultados
          </>
        )}
      </div>

      <div className="order-1 flex items-center gap-1 sm:order-2">
        <Button
          aria-label="Página anterior"
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={isFetching || !companyData || currentPage <= 1}
          onClick={goToPreviousPage}
          size="icon"
          variant="outline"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {isFetching ? (
          renderSkeletonPagination()
        ) : (
          <div className="flex items-center">{renderPaginationButtons()}</div>
        )}

        <Button
          aria-label="Página siguiente"
          className="h-8 w-8 hover:bg-accent hover:text-accent-foreground"
          disabled={
            isFetching || !companyData || currentPage >= companyData.totalPages
          }
          onClick={goToNextPage}
          size="icon"
          variant="outline"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  )
}
